import { useEffect, useState, useRef } from 'react'
import styled from 'styled-components'
import { useSelector, useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import { Box, Button, Grid, Theme } from '@mui/material'
import { AWS_LOGIN_URL, AWS_CHALLENGE_RECALCULATE_URL } from 'config'
import LinkButton from 'components/ui/LinkButton'
import LoadingSpinnerGreen from 'components/ui/LoadingSpinnerGreen'
import PopoverMessage from 'components/ui/PopoverMessage'
import ChallengeLeaderboard from 'components/challenge/ChallengeLeaderboard'
import ExportButton from 'components/form/ExportButton'
import { clearChallenge, selectChallenge } from 'store/challenge/slice'
import { addChallengePlayer, getChallenge, getChallengeLeaderboardReportV1 } from 'store/challenge/thunks'
import InputField from 'components/form/InputField'
import SubmitButton from 'components/form/SubmitButton'
import { useForm } from 'react-hook-form'
import PageHeader from 'components/challenge/PageHeader'
import { makeStyles } from '@mui/styles'
import { rem } from 'theme/materialUITheme'

const useStyles = makeStyles((theme: Theme) => ({
  actionButtons: {
    margin: rem(10),
    display: 'flex',
    gap: rem(8),
    flexWrap: 'wrap',
    paddingRight: '20px',
    '& button, a': {
      backgroundColor: '#1ca892',
      color: theme.customPalette.light,
      fontSize: '0.9rem',
      margin: '5px 0',
      width: '100%',
      maxWidth: '200px',
      borderRadius: '5px',
      height: rem(48),
      '&:first-of-type': {
        backgroundColor: theme.palette.primary.main,
      },
      '&:nth-of-type(2)': {
        backgroundColor: '#1b62a8',
      },
    },
  },
  pageContainer: {
    margin: rem(10),
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    paddingTop: '16px',
  },
  addPlayerButton: {
    border: `2px solid ${theme.palette.primary.main}`,
    marginLeft: rem(10),
    marginTop: rem(22),
    height: rem(48),
  },
  userIdInput: {
    width: '60%',
  },
}))
interface ParamTypes extends ParamTypesBase {
  id: string
}

export default function Challenge(): JSX.Element {
  const { id: paramId } = useParams<ParamTypes>()
  const id = paramId || ''
  const classes = useStyles()
  const [currentId, setCurrentId] = useState<string>('')
  const [popoverOpen, setPopoverOpen] = useState<boolean>(false)
  const popoverRef = useRef(null)
  const { challenge, status, exportResultsStatus } = useSelector(selectChallenge)
  const {
    register,
    handleSubmit,
    formState: { isDirty, isValid },
  } = useForm<ChallengeAddPlayerForm>()

  const dispatch = useDispatch()
  const intl = useIntl()

  useEffect(() => {
    if ((id && status === 'idle') || currentId !== id) {
      dispatch(clearChallenge())
      setCurrentId(id)
      dispatch(getChallenge(id))
    }
  }, [id, currentId, status, dispatch])

  const exportLeaderboardResults = () => {
    if (!challenge.challengeId || challenge.challengeId === '-1') {
      return
    }
    dispatch(getChallengeLeaderboardReportV1(challenge.challengeId))
  }

  const submitAddPlayer = (data: ChallengeAddPlayerForm) => {
    if (challenge.challengeId) {
      dispatch(
        addChallengePlayer({
          challengeId: challenge.challengeId,
          limit: challenge.participantCount ? challenge.participantCount + 1 : 1,
          playerId: data.playerId,
        }),
      )
    }
  }

  const handlePopoverToggle = () => {
    setPopoverOpen(!popoverOpen)
  }

  if (status === 'loading') {
    return (
      <SpinnerContainer>
        <LoadingSpinnerGreen size={72} />
      </SpinnerContainer>
    )
  }

  const getRecalcMessage = () => {
    return (
      <RecalcMessage>
        <h5>{intl.formatMessage({ id: 'challenges.recalculateMessage' })}</h5>
        <a href={AWS_LOGIN_URL} target="_blank" rel="noopener noreferrer">
          {intl.formatMessage({ id: 'strings.login' })}
        </a>
        <a href={AWS_CHALLENGE_RECALCULATE_URL} target="_blank" rel="noopener noreferrer">
          {intl.formatMessage({ id: 'strings.recalculate' })}
        </a>
      </RecalcMessage>
    )
  }

  return (
    <Box className={classes.pageContainer}>
      <PageHeader challenge={challenge} status={status} />
      <Grid container>
        <Grid xs={9}>
          <Box className={classes.actionButtons}>
            <LinkButton
              to={`/admin/challenges/${challenge.challengeId}/edit`}
              text={<FormattedMessage id="strings.edit" />}
              fullWidth
            />
            <Button onClick={handlePopoverToggle} ref={popoverRef}>
              <FormattedMessage id="strings.recalculate" />
            </Button>
            <PopoverMessage
              id="recalc-challenge"
              open={popoverOpen}
              text={getRecalcMessage()}
              anchorEl={popoverRef.current}
              handleClose={handlePopoverToggle}
            />
            <ExportButton
              onClick={exportLeaderboardResults}
              status={exportResultsStatus}
              text={<FormattedMessage id="strings.exportResults" />}
            />
            <Button disabled>
              <FormattedMessage id="strings.exportRounds" />
            </Button>
          </Box>
        </Grid>
        <Grid xs={3} mt={1}>
          <form onSubmit={handleSubmit(submitAddPlayer)}>
            <InputField
              className={classes.userIdInput}
              register={register}
              name="playerId"
              type="number"
              label={intl.formatMessage({ id: 'strings.userId' })}
              outlined
            />
            <SubmitButton
              className={classes.addPlayerButton}
              status={'idle'}
              text={intl.formatMessage({ id: 'challenges.addPlayer' })}
              disabled={!isDirty || !isValid}
            />
          </form>
        </Grid>
        <ChallengeLeaderboard />
      </Grid>
    </Box>
  )
}

const SpinnerContainer = styled.div`
  display: flex;
  width: 100%;
  margin: 50px auto;
  justify-content: center;
`

const RecalcMessage = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  & > a {
    color: ${(props) => props.theme.palette.primary.main};
    text-decoration: none;
    margin: 20px 0 0;

    &:hover {
      opacity: 0.85;
    }
    &:visited {
      color: ${(props) => props.theme.palette.primary.main};
    }
  }
`
